import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import logo from '../../assets/images/logo-sq.jpg';
import { selectCurrentUser } from "../../Redux/Slices/authSlice";
import { useDashboardQuery } from "../../Redux/Apis/authApi";
import { fa_dateـetime, fa_number } from "../../utils/Persian";
import Icon from '../../Components/Icon';
import { useSearchExamsQuery } from "../../Redux/Apis/examApi";

export default function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const { data: dashboardData, isFetching: statusLoading } = useDashboardQuery();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (!trimmed || trimmed.length < 2) {
      setDebouncedQuery('');
      return;
    }
    const timeout = setTimeout(() => {
      setDebouncedQuery(trimmed);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const {
    data: searchData,
    isFetching: searchLoading,
  } = useSearchExamsQuery(debouncedQuery, {
    skip: !debouncedQuery,
  });

  const searchResults = useMemo(() => searchData ?? [], [searchData]);

  const minSearchLength = 2;
  const trimmedSearch = searchTerm.trim();
  const hasValidSearch = trimmedSearch.length >= minSearchLength;

  const showSearchDropdown = isSearchFocused && !!debouncedQuery;

  const goToSearchPage = () => {
    setIsSearchFocused(false);
    if (!hasValidSearch) {
      navigate('/panel/exams');
      return;
    }
    navigate(`/panel/exams?search=${encodeURIComponent(trimmedSearch)}`);
  };

  const logOut = () => {
    dispatch({ type: 'USER_LOGOUT' });
    navigate('/panel/login');
  };

  const initials = user ? `${user.name?.[0] ?? ''}${user.family?.[0] ?? ''}`.trim() : '';

  return (
    <header className="container-fluid py-3">
      {/* Mobile layout (md-) */}
      <div className="d-md-none">
        {/* Row 1: menu + logo (right), profile (left) */}
        <div className="d-flex align-items-center gap-2">
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-light border"
              type="button"
              data-bs-toggle="offcanvas"
              data-bs-target="#sidebarMenu"
              aria-controls="sidebarMenu"
              aria-expanded="false"
              aria-label="باز کردن منو"
            >
              <Icon name="list" size={18} />
            </button>

            <Link to="/" className="d-flex align-items-center gap-2 text-decoration-none">
              <img src={logo} alt="GREhub" width={40} height={40} className="rounded-3 border" />
              <strong className="text-danger"><span className="text-black">GRE</span>hub</strong>
            </Link>
          </div>

          <div className="dropdown ms-auto">
            <button className="btn btn-light border d-flex align-items-center gap-2" data-bs-toggle="dropdown">
              <span className="panel-avatar">
                {initials || <Icon name="person" size={18} />}
              </span>
              <span className="d-none d-sm-inline">{`${user?.name ?? ''} ${user?.family ?? ''}`.trim() || 'کاربر'}</span>
              <Icon name="chevron-down" size={16} />
            </button>
            <ul className="dropdown-menu dropdown-menu-end shadow-sm">
              <li>
                <span className="dropdown-item-text text-muted small">
                  {statusLoading ? 'در حال بارگذاری…' : `${user?.name ?? ''} ${user?.family ?? ''}`.trim() || 'کاربر GREhub'}
                </span>
              </li>
              <li><hr className="dropdown-divider" /></li>
              <li className="dropdown-item-text small d-flex align-items-center gap-2">
                <Icon name="wallet" className="text-warning" size={18} strokeWidth={1.4} />
                <span>
                  اعتبار باقی‌مانده: {fa_number(dashboardData?.status?.credit ?? 0)}
                </span>
              </li>
              <li className="dropdown-item-text small d-flex align-items-center gap-2">
                <Icon
                  name={dashboardData?.status?.premium ? 'badge-check' : 'person'}
                  className={dashboardData?.status?.premium ? 'text-success' : 'text-secondary'}
                  size={18}
                  strokeWidth={dashboardData?.status?.premium ? 1.4 : 1.6}
                />
                <span>
                  نوع حساب: {dashboardData?.status?.premium ? 'پریمیوم' : 'معمولی'}
                </span>
              </li>
              {dashboardData?.status?.premium && dashboardData?.status?.expire && <li className="dropdown-item-text small d-flex align-items-center gap-2">
                <Icon name="calendar-check" className="text-danger" size={18} strokeWidth={1.3} />
                <span>
                  {`انقضا: ${fa_dateـetime(dashboardData.status.expire)}`}
                </span>
              </li>}
              <li><hr className="dropdown-divider" /></li>
              <li>
                <button type="button" className="dropdown-item d-flex align-items-center gap-2" onClick={logOut}>
                  <Icon name="box-arrow-right" size={18} strokeWidth={1.4} />
                  خروج
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Row 2: full-width search */}
        <div className="mt-2">
          <div className="panel-search position-relative">
            <button
              type="button"
              className="btn btn-link p-0 border-0 text-secondary d-flex align-items-center"
              onMouseDown={(e) => {
                e.preventDefault();
                goToSearchPage();
              }}
            >
              <Icon name="search" className="text-secondary" size={18} />
            </button>
            <input
              className="form-control"
              placeholder="جستجو در آزمون‌ها…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => {
                // Slight delay to allow click on dropdown items
                setTimeout(() => setIsSearchFocused(false), 150);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  goToSearchPage();
                }
              }}
            />
            {showSearchDropdown && (
              <div
                className="panel-search-dropdown"
                onMouseDown={(e) => {
                  // Prevent input blur before navigation
                  e.preventDefault();
                }}
              >
                {searchLoading && (
                  <div className="panel-search-item text-muted small">
                    در حال جستجو…
                  </div>
                )}
                {!searchLoading && searchResults.length === 0 && (
                  <div className="panel-search-item text-muted small">
                    نتیجه‌ای یافت نشد.
                  </div>
                )}
                  {!searchLoading && searchResults.map((exam) => (
                    <button
                      key={exam.id}
                      type="button"
                      className="panel-search-item text-start"
                      onClick={() => {
                      navigate(`/panel/exams?search=${encodeURIComponent(exam.name)}`);
                      setIsSearchFocused(false);
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-bold small">{exam.name}</span>
                      {exam.is_free && (
                        <span className="badge bg-success-subtle text-success ms-2">رایگان</span>
                      )}
                    </div>
                    <div className="small text-muted mt-1">
                      {exam.ExamCategory?.name ?? 'بدون دسته‌بندی'} • {fa_number(exam.time)} دقیقه
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop/Tablet layout (md+) — original */}
      <div className="d-none d-md-block">
        <div className="row g-3 align-items-center">
          <div className="col-auto d-lg-none">
            <button
              className="btn btn-light border"
              type="button"
              data-bs-toggle="offcanvas"
              data-bs-target="#sidebarMenu"
              aria-controls="sidebarMenu"
              aria-expanded="false"
              aria-label="باز کردن منو"
            >
              <Icon name="list" size={18} />
            </button>
          </div>

          <div className="col-auto">
            <div className="d-flex align-items-center gap-2">
              <Link to="/" className="d-flex align-items-center gap-2 text-decoration-none">
                <img src={logo} alt="GREhub" width={44} height={44} className="rounded-3 border" />
                <strong className="fs-5 text-danger"><span className="text-black">GRE</span>hub</strong>
              </Link>
            </div>
          </div>

          <div className="col">
            <div className="panel-search position-relative">
              <button
                type="button"
                className="btn btn-link p-0 border-0 text-secondary d-flex align-items-center"
                onMouseDown={(e) => {
                  e.preventDefault();
                  goToSearchPage();
                }}
              >
                <Icon name="search" className="text-secondary" size={18} />
              </button>
              <input
                className="form-control"
                placeholder="جستجو در آزمون‌ها…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => {
                  setTimeout(() => setIsSearchFocused(false), 150);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    goToSearchPage();
                  }
                }}
              />
              {showSearchDropdown && (
                <div
                  className="panel-search-dropdown"
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                >
                  {searchLoading && (
                    <div className="panel-search-item text-muted small">
                      در حال جستجو…
                    </div>
                  )}
                  {!searchLoading && searchResults.length === 0 && (
                    <div className="panel-search-item text-muted small">
                      نتیجه‌ای یافت نشد.
                    </div>
                  )}
                  {!searchLoading && searchResults.map((exam) => (
                    <button
                      key={exam.id}
                      type="button"
                      className="panel-search-item text-start"
                      onClick={() => {
                        navigate(`/panel/exams?search=${encodeURIComponent(exam.name)}`);
                        setIsSearchFocused(false);
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-bold small">{exam.name}</span>
                        {exam.is_free && (
                          <span className="badge bg-success-subtle text-success ms-2">رایگان</span>
                        )}
                      </div>
                      <div className="small text-muted mt-1">
                        {exam.ExamCategory?.name ?? 'بدون دسته‌بندی'} • {fa_number(exam.time)} دقیقه
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="col-auto d-none d-md-inline-flex">
            <Link to="/panel/referral" className="btn btn-outline-primary">
              <Icon name="megaphone" size={18} className="me-1" strokeWidth={1.4} />
              معرفی و کسب اعتبار
            </Link>
          </div>

          <div className="col-auto">
            <div className="dropdown">
              <button className="btn btn-light border d-flex align-items-center gap-2" data-bs-toggle="dropdown">
                <span className="panel-avatar">
                  {initials || <Icon name="person" size={18} />}
                </span>
                <span className="d-none d-sm-inline">{`${user?.name ?? ''} ${user?.family ?? ''}`.trim() || 'کاربر'}</span>
                <Icon name="chevron-down" size={16} />
              </button>
              <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                <li>
                  <span className="dropdown-item-text text-muted small">
                    {statusLoading ? 'در حال بارگذاری…' : `${user?.name ?? ''} ${user?.family ?? ''}`.trim() || 'کاربر GREhub'}
                  </span>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li className="dropdown-item-text small d-flex align-items-center gap-2">
                  <Icon name="wallet" className="text-warning" size={18} strokeWidth={1.4} />
                  <span>
                    اعتبار باقی‌مانده: {fa_number(dashboardData?.status?.credit ?? 0)}
                  </span>
                </li>
                <li className="dropdown-item-text small d-flex align-items-center gap-2">
                  <Icon
                    name={dashboardData?.status?.premium ? 'badge-check' : 'person'}
                    className={dashboardData?.status?.premium ? 'text-success' : 'text-secondary'}
                    size={18}
                    strokeWidth={dashboardData?.status?.premium ? 1.4 : 1.6}
                  />
                  <span>
                    نوع حساب: {dashboardData?.status?.premium ? 'پریمیوم' : 'معمولی'}
                  </span>
                </li>
                {dashboardData?.status?.premium && dashboardData?.status?.expire && <li className="dropdown-item-text small d-flex align-items-center gap-2">
                  <Icon name="calendar-check" className="text-danger" size={18} strokeWidth={1.3} />
                  <span>
                    {`انقضا: ${fa_dateـetime(dashboardData.status.expire)}`}
                  </span>
                </li>}
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button type="button" className="dropdown-item d-flex align-items-center gap-2" onClick={logOut}>
                    <Icon name="box-arrow-right" size={18} strokeWidth={1.4} />
                    خروج
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
